import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { adminFetchInit } from '../utils/adminToken';

const EDIT_PRODUCT_LABELS = { tshirt: '👕 T-Shirt', mug: '☕ Mug', poster: '🖼️ Poster', totebag: '👜 Tote Bag', casquette: '🧢 Cap' };
const REVERSE_LABEL_MAP = {};
Object.entries(EDIT_PRODUCT_LABELS).forEach(([k, v]) => { REVERSE_LABEL_MAP[v] = k; });

/** Dark-theme floating label for number inputs (peer + placeholder-shown). */
function FloatingLabeledNumber({ id, label, value, onChange, prefix, min = '0', step = '1', accentRing = 'emerald' }) {
  const ring =
    accentRing === 'purple'
      ? 'focus:border-purple-500/50 focus:ring-purple-500/20'
      : accentRing === 'amber'
        ? 'focus:border-amber-500/50 focus:ring-amber-500/20'
        : 'focus:border-emerald-500/50 focus:ring-emerald-500/20';
  const labelFocus =
    accentRing === 'purple'
      ? 'peer-focus:text-purple-400'
      : accentRing === 'amber'
        ? 'peer-focus:text-amber-400'
        : 'peer-focus:text-emerald-400';
  return (
    <div className="relative">
      {prefix ? (
        <span className="pointer-events-none absolute left-3 bottom-[11px] z-20 text-sm font-medium text-zinc-500">{prefix}</span>
      ) : null}
      <input
        id={id}
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={onChange}
        className={`peer block w-full rounded-lg border border-zinc-600 bg-zinc-950/90 py-3 text-sm text-zinc-100 shadow-inner placeholder:text-transparent focus:outline-none focus:ring-2 ${ring} ${prefix ? 'pl-8 pr-3 pt-5 pb-2' : 'px-3 pt-5 pb-2'}`}
        placeholder=" "
      />
      <label
        htmlFor={id}
        className={`pointer-events-none absolute ${prefix ? 'left-8' : 'left-3'} top-1/2 z-10 -translate-y-1/2 cursor-text text-sm text-zinc-500 transition-all duration-200 peer-focus:top-2 peer-focus:-translate-y-0 peer-focus:text-[11px] ${labelFocus} peer-[:not(:placeholder-shown)]:top-2 peer-[:not(:placeholder-shown)]:-translate-y-0 peer-[:not(:placeholder-shown)]:text-[11px] peer-[:not(:placeholder-shown)]:text-zinc-400`}
      >
        {label}
      </label>
    </div>
  );
}

const EditProductModal = ({ isOpen, onClose, onProductUpdated, product, enableVendorEdit = false }) => {
  const modalRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [images, setImages] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    comparePrice: '',
    costPerItem: '',
    category: '',
    vendor: '',
    tags: '',
    stock: '0',
    weight: '0.0',
    weightUnit: 'kg',
    status: 'active',
    trackQuantity: true,
    continueSellingOutOfStock: false,
    hasSKU: false,
    sku: '',
    barcode: '',
    isPhysicalProduct: true,
    chargeTax: true
  });

  const [typePrices, setTypePrices] = useState({});
  const [typeStock, setTypeStock] = useState({});
  const [variantIdMap, setVariantIdMap] = useState({});

  const defaultQualityPrices = (base) => {
    const b = parseFloat(base) || 0;
    return { standard: base, premium: (b * 1.20).toFixed(2), deluxe: (b * 1.40).toFixed(2) };
  };

  const variantQty = (v) => {
    const q = v?.inventory_quantity;
    if (q != null && q !== '') return Number(q);
    const q2 = v?.quantityAvailable;
    if (q2 != null && q2 !== '') return Number(q2);
    return 0;
  };

  // Parse product variants into per-type pricing + per-variant available qty
  const parseVariantsToTypePrices = useCallback((prod) => {
    const variants = prod?.variants || [];
    const prices = {};
    const stockMap = {};
    const idMap = {};

    const setVariantPrice = (typeKey, quality, price, variantId, invQty) => {
      if (!prices[typeKey]) prices[typeKey] = { standard: '0.00', premium: '0.00', deluxe: '0.00' };
      if (!stockMap[typeKey]) stockMap[typeKey] = { standard: '0', premium: '0', deluxe: '0' };
      if (!idMap[typeKey]) idMap[typeKey] = {};
      const q = quality.toLowerCase();
      if (q === 'standard' || q === 'premium' || q === 'deluxe') {
        prices[typeKey][q] = price || '0.00';
        if (invQty != null && invQty !== '') {
          stockMap[typeKey][q] = String(Math.max(0, Math.floor(Number(invQty)) || 0));
        }
        idMap[typeKey][q] = variantId;
      }
    };

    const hasSelectedOptions = variants.some(v => v.selectedOptions?.length > 0);

    if (hasSelectedOptions) {
      const hasTypeOption = variants.some(v => v.selectedOptions?.some(o => o.name === 'Type'));
      if (hasTypeOption) {
        variants.forEach(v => {
          const typeOpt = v.selectedOptions?.find(o => o.name === 'Type');
          const qualityOpt = v.selectedOptions?.find(o => o.name === 'Quality');
          if (!typeOpt) return;
          const typeKey = REVERSE_LABEL_MAP[typeOpt.value] || typeOpt.value;
          setVariantPrice(typeKey, qualityOpt?.value || 'Standard', v.price, v.id, variantQty(v));
        });
      } else {
        variants.forEach(v => {
          const qualityOpt = v.selectedOptions?.find(o => o.name === 'Quality');
          const quality = qualityOpt?.value || v.selectedOptions?.[0]?.value || 'Standard';
          setVariantPrice('_single', quality, v.price, v.id, variantQty(v));
        });
      }
    } else {
      const hasTypeOption = variants.some(v => v.option1 && v.option2);
      if (hasTypeOption) {
        variants.forEach(v => {
          const typeKey = REVERSE_LABEL_MAP[v.option1] || v.option1;
          setVariantPrice(typeKey, v.option2 || 'Standard', v.price, v.id, variantQty(v));
        });
      } else {
        variants.forEach(v => {
          setVariantPrice('_single', v.option1 || 'Standard', v.price, v.id, variantQty(v));
        });
      }
    }

    // Fill missing quality prices with defaults (only for t-shirt)
    Object.keys(prices).forEach(k => {
      const p = prices[k];
      const base = parseFloat(p.standard) || 0;
      if (k === 'tshirt') {
        if (!p.premium || p.premium === '0.00') p.premium = (base * 1.20).toFixed(2);
        if (!p.deluxe || p.deluxe === '0.00') p.deluxe = (base * 1.40).toFixed(2);
      }
    });

    // If no types found but product has availability data, expand
    if (Object.keys(prices).length === 0 || (Object.keys(prices).length === 1 && prices['_single'] !== undefined)) {
      const productAvail = prod?.products;
      if (productAvail && typeof productAvail === 'object' && Object.keys(productAvail).length > 0) {
        const isAll = Object.values(productAvail).every(v => v);
        const availKeys = isAll ? Object.keys(EDIT_PRODUCT_LABELS) : Object.keys(EDIT_PRODUCT_LABELS).filter(k => productAvail[k] === true);
        if (availKeys.length > 0) {
          const singlePrices = prices['_single'] || defaultQualityPrices(prod?.price || '0.00');
          const singleStock = stockMap['_single'] || { standard: '0', premium: '0', deluxe: '0' };
          delete prices['_single'];
          delete stockMap['_single'];
          availKeys.forEach(k => {
            if (!prices[k]) prices[k] = { ...singlePrices };
            if (!stockMap[k]) stockMap[k] = { ...singleStock };
          });
        }
      }
    }

    // Final fallback
    if (Object.keys(prices).length === 0) {
      prices['_single'] = defaultQualityPrices(prod?.price || '0.00');
      const s = String(prod?.stock != null ? prod.stock : 0);
      stockMap['_single'] = { standard: s, premium: s, deluxe: s };
    }

    // Align stock rows with price rows (new types may lack stock entries)
    Object.keys(prices).forEach((k) => {
      if (!stockMap[k]) stockMap[k] = { standard: '0', premium: '0', deluxe: '0' };
      ['standard', 'premium', 'deluxe'].forEach((slot) => {
        if (stockMap[k][slot] == null || stockMap[k][slot] === '') stockMap[k][slot] = '0';
      });
    });

    return { prices, idMap, stockMap };
  }, []);

  // Populate form with product data when product changes
  useEffect(() => {
    if (product) {
      const firstVariant = product.variants?.[0];
      const invMgmt = firstVariant?.inventory_management;
      const trackQty = product.inventoryTracked ?? (invMgmt === 'shopify');

      setFormData({
        title: product.name || '',
        description: product.description?.replace(/<[^>]*>/g, '') || '',
        comparePrice: firstVariant?.compare_at_price || '',
        costPerItem: firstVariant?.cost || '',
        category: product.category || '',
        vendor: product.vendor || '',
        tags: product.tags || '',
        stock: product.stock?.toString() || '0',
        weight: firstVariant?.weight?.toString() || '0.0',
        weightUnit: firstVariant?.weight_unit || 'kg',
        status: product.status || 'active',
        trackQuantity: trackQty ?? true,
        continueSellingOutOfStock: firstVariant?.inventory_policy === 'continue',
        hasSKU: !!(firstVariant?.sku),
        sku: firstVariant?.sku || '',
        barcode: firstVariant?.barcode || '',
        isPhysicalProduct: firstVariant?.requires_shipping !== false,
        chargeTax: firstVariant?.taxable !== false
      });

      const { prices, idMap, stockMap } = parseVariantsToTypePrices(product);
      setTypePrices(prices);
      setVariantIdMap(idMap);
      setTypeStock(stockMap);

      if (product.images && product.images.length > 0) {
        setImages(product.images.filter(img => img.src).map(img => ({
          url: img.src,
          alt: img.alt || product.name,
          existing: true
        })));
      } else {
        setImages([]);
      }
    }
  }, [product, parseVariantsToTypePrices]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle image upload (placeholder - would integrate with actual image upload service)
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    // In a real implementation, you would upload these to a service like Cloudinary
    // For now, we'll just create object URLs for preview
    const newImages = files.slice(0, 5 - images.length).map(file => ({
      url: URL.createObjectURL(file),
      alt: formData.title,
      file: file
    }));
    setImages(prev => [...prev, ...newImages]);
  };

  // Remove image
  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  // Reset form
  const resetForm = useCallback(() => {
    if (product) {
      const firstVariant = product.variants?.[0];
      const invMgmt = firstVariant?.inventory_management;
      const trackQty = product.inventoryTracked ?? (invMgmt === 'shopify');
      setFormData({
        title: product.name || '',
        description: product.description?.replace(/<[^>]*>/g, '') || '',
        comparePrice: firstVariant?.compare_at_price || '',
        costPerItem: firstVariant?.cost || '',
        category: product.category || '',
        vendor: product.vendor || '',
        tags: product.tags || '',
        stock: product.stock?.toString() || '0',
        weight: firstVariant?.weight?.toString() || '0.0',
        weightUnit: firstVariant?.weight_unit || 'kg',
        status: product.status || 'active',
        trackQuantity: trackQty ?? true,
        continueSellingOutOfStock: firstVariant?.inventory_policy === 'continue',
        hasSKU: !!(firstVariant?.sku),
        sku: firstVariant?.sku || '',
        barcode: firstVariant?.barcode || '',
        isPhysicalProduct: firstVariant?.requires_shipping !== false,
        chargeTax: firstVariant?.taxable !== false
      });

      const { prices, idMap, stockMap } = parseVariantsToTypePrices(product);
      setTypePrices(prices);
      setVariantIdMap(idMap);
      setTypeStock(stockMap);

      if (product.images && product.images.length > 0) {
        setImages(product.images.filter(img => img.src).map(img => ({
          url: img.src,
          alt: img.alt || product.name,
          existing: true
        })));
      } else {
        setImages([]);
      }
    }
    setSubmitError(null);
  }, [product, parseVariantsToTypePrices]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      console.log('📝 Submitting product update...');
      
      let uploadedImages = [];
      
      // First, upload any new images to Cloudinary if there are any
      const blobImages = images.filter(img => img.url && img.url.startsWith('blob:'));
      if (blobImages.length > 0) {
        console.log('🖼️ Uploading new images to Cloudinary...');
        
        try {
          // Create FormData to upload images
          const formData_upload = new FormData();
          
          // Add product title for alt text
          formData_upload.append('title', formData.title);
          
          // Add each blob image as a file
          for (let i = 0; i < blobImages.length; i++) {
            const imageData = blobImages[i];
            if (imageData.file) {
              formData_upload.append(`image_${i}`, imageData.file);
            }
          }
          
          // Upload to Cloudinary
          const uploadResponse = await fetch('/.netlify/functions/product-image-upload', adminFetchInit({
            method: 'POST',
            body: formData_upload
          }));
          
          const uploadResult = await uploadResponse.json();
          
          if (uploadResult.success) {
            uploadedImages = uploadResult.images;
            console.log('✅ New images uploaded successfully:', uploadedImages);
          } else {
            console.warn('⚠️ Image upload failed:', uploadResult.error);
          }
        } catch (uploadError) {
          console.warn('⚠️ Image upload error:', uploadError);
          // Continue without new images - don't fail the entire product update
        }
      }
      
      // Prepare product data matching Shopify REST API format
      const allImages = [
        // Existing images (not blob URLs)
        ...images.filter(img => img.url && !img.url.startsWith('blob:')),
        // Newly uploaded images
        ...uploadedImages
      ];

      const qualityKeyMap = { Standard: 'standard', Premium: 'premium', Deluxe: 'deluxe' };
      const typeEntries = Object.entries(typePrices);
      const isSingleType = typeEntries.length === 1 && typeEntries[0][0] === '_single';
      const hasTshirt = typeEntries.some(([k]) => k === 'tshirt');

      const sharedVariantProps = {
        weight: parseFloat(formData.weight),
        weight_unit: formData.weightUnit,
        inventory_management: formData.trackQuantity ? 'shopify' : null,
        inventory_policy: formData.continueSellingOutOfStock ? 'continue' : 'deny',
        compare_at_price: formData.comparePrice || null,
        cost: formData.costPerItem || null,
        taxable: formData.chargeTax,
        requires_shipping: formData.isPhysicalProduct
      };

      const variants = [];
      typeEntries.forEach(([typeKey, qPrices]) => {
        const typeLabel = isSingleType ? null : (EDIT_PRODUCT_LABELS[typeKey] || typeKey);
        const isTshirt = typeKey === 'tshirt';
        const qualities = (isTshirt && hasTshirt) ? ['Standard', 'Premium', 'Deluxe'] : ['Standard'];
        qualities.forEach(quality => {
          const price = qPrices[qualityKeyMap[quality]] || qPrices.standard || '0.00';
          const slot = qualityKeyMap[quality];
          const existingId = variantIdMap[typeKey]?.[slot];
          const invRaw = typeStock[typeKey]?.[slot] ?? '0';
          const inventory_quantity = Math.max(0, parseInt(String(invRaw).replace(/\D/g, '') || '0', 10) || 0);
          const variant = {
            ...sharedVariantProps,
            inventory_quantity,
            price,
            sku: formData.hasSKU && formData.sku
              ? (hasTshirt && isTshirt ? `${formData.sku}-${typeKey}-${quality.toLowerCase()}` : `${formData.sku}-${typeKey}`)
              : null,
            barcode: formData.barcode || null
          };
          if (existingId) variant.id = existingId;
          if (isSingleType) {
            variant.option1 = quality;
          } else {
            variant.option1 = typeLabel;
            if (hasTshirt) variant.option2 = quality;
          }
          variants.push(variant);
        });
      });

      const options = isSingleType
        ? [{ name: 'Quality', values: ['Standard', 'Premium', 'Deluxe'] }]
        : hasTshirt
          ? [
              { name: 'Type', values: typeEntries.map(([k]) => EDIT_PRODUCT_LABELS[k] || k) },
              { name: 'Quality', values: ['Standard', 'Premium', 'Deluxe'] }
            ]
          : [{ name: 'Type', values: typeEntries.map(([k]) => EDIT_PRODUCT_LABELS[k] || k) }];

      const productData = {
        productId: product.shopifyId || product.id,
        product: {
          id: product.shopifyId || product.id,
          title: formData.title,
          body_html: formData.description ? `<p>${formData.description}</p>` : '',
          vendor: formData.vendor,
          product_type: formData.category,
          status: (formData.status || 'active').toLowerCase(),
          tags: formData.tags,
          options,
          variants
        }
      };

      // Add images to product data
      if (allImages.length > 0) {
        productData.product.images = allImages.map(img => ({
          src: img.src || img.url,
          alt: img.alt || formData.title
        }));
      }

      console.log('📋 Product update data:', productData);

      const response = await fetch('/.netlify/functions/shopify-edit-product', adminFetchInit({
        method: 'PUT',
        body: JSON.stringify(productData)
      }));

      const result = await response.json();

      if (result.success) {
        console.log('✅ Product updated successfully:', result.product);
        
        // Reset form and close modal
        resetForm();
        onClose();
        
        // Notify parent component
        if (onProductUpdated) {
          onProductUpdated(result.product);
        }
      } else {
        throw new Error(result.error || 'Failed to update product');
      }
    } catch (error) {
      console.error('❌ Error updating product:', error);
      setSubmitError(error.message || 'Failed to update product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  }, [isSubmitting, resetForm, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, isSubmitting, handleClose]);

  // Handle click outside modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, isSubmitting, handleClose]);

  const totalAvailableByVariant = useMemo(() => {
    return Object.values(typeStock).reduce((sum, row) => {
      if (!row) return sum;
      return sum + (parseInt(row.standard, 10) || 0) + (parseInt(row.premium, 10) || 0) + (parseInt(row.deluxe, 10) || 0);
    }, 0);
  }, [typeStock]);

  // Fetch vendor options when modal opens (only if vendor editing is enabled)
  useEffect(() => {
    const fetchVendorOptions = async () => {
      if (!isOpen || !enableVendorEdit) return;

      setLoadingVendors(true);
      try {
        const response = await fetch('/.netlify/functions/get-websites');
        const result = await response.json();
        
        if (result.success) {
          setVendorOptions(result.websites || []);
        } else {
          console.error('Failed to fetch vendor options:', result.error);
          setVendorOptions([]);
        }
      } catch (error) {
        console.error('Error fetching vendor options:', error);
        setVendorOptions([]);
      } finally {
        setLoadingVendors(false);
      }
    };

    fetchVendorOptions();
  }, [isOpen, enableVendorEdit]);

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-zinc-950/75 p-4 backdrop-blur-md">
      <div
        ref={modalRef}
        className="flex max-h-[95vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 shadow-2xl shadow-black/40"
      >
        <header className="shrink-0 border-b border-zinc-800 bg-zinc-900/95 px-6 py-5 sm:px-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-emerald-500/20 bg-emerald-500/10">
                <svg className="h-6 w-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold tracking-tight text-zinc-50 sm:text-2xl">Edit product</h2>
                <p className="mt-0.5 text-sm text-zinc-500">Update listing, pricing, inventory, and organization</p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="rounded-lg p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-100 disabled:opacity-50"
              aria-label="Close dialog"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        <div className="sidebar-scroll min-h-0 flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 sm:p-8">
            {submitError && (
              <div className="mb-6 flex items-start gap-3 rounded-xl border border-red-500/30 bg-red-950/40 px-4 py-3 text-red-200">
                <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-red-500/20">
                  <svg className="h-4 w-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-100">Couldn&apos;t save changes</p>
                  <p className="mt-1 text-sm text-red-200/90">{submitError}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              <div className="space-y-8 lg:col-span-2">
                <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
                  <div className="mb-5 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-800 text-zinc-400">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                    <h3 className="text-base font-semibold text-zinc-100">Basic information</h3>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-zinc-500">Product title</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                        disabled
                        className="w-full cursor-not-allowed rounded-lg border border-zinc-700 bg-zinc-950/80 px-4 py-3 text-base font-medium text-zinc-500 opacity-70"
                        placeholder="Loaded from submission data"
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-zinc-500">Description</label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={5}
                        className="w-full resize-none rounded-lg border border-zinc-700 bg-zinc-950/80 px-4 py-3 text-sm text-zinc-100 placeholder-zinc-600 transition-colors focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                        placeholder="Describe your product..."
                      />
                    </div>
                  </div>
                </section>

                <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
                  <div className="mb-5 flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-800 text-zinc-400">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-zinc-100">Media</h3>
                      <p className="text-xs text-zinc-500">Up to 5 images — JPG, PNG, WebP</p>
                    </div>
                  </div>

                <div className="rounded-xl border border-dashed border-zinc-700 p-6 transition-colors hover:border-emerald-500/40">
                  {images.length === 0 ? (
                    <div className="text-center">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload-edit"
                        disabled={isSubmitting}
                      />
                      <label htmlFor="image-upload-edit" className="group cursor-pointer">
                        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl border border-zinc-700 bg-zinc-800/80 transition-colors group-hover:border-emerald-500/40 group-hover:bg-emerald-500/5">
                          <svg className="h-7 w-7 text-emerald-500/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-zinc-300">Upload images</p>
                        <p className="mt-1 text-xs text-zinc-500">Click to browse · max 10MB each</p>
                      </label>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                      {images.map((image, index) => (
                        <div key={index} className="group relative">
                          <div className="aspect-square overflow-hidden rounded-xl border border-zinc-700">
                            <img
                              src={image.url}
                              alt={image.alt}
                              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                            />
                          </div>
                          {image.existing && (
                            <span className="absolute left-2 top-2 rounded-md bg-emerald-600/90 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                              Current
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -right-1.5 -top-1.5 flex h-8 w-8 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-transform hover:scale-105 hover:bg-red-500"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}

                      {images.length < 5 && (
                        <div className="flex aspect-square items-center justify-center rounded-xl border-2 border-dashed border-zinc-700 transition-colors hover:border-emerald-500/50">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                            id="image-upload-add-edit"
                            disabled={isSubmitting}
                          />
                          <label htmlFor="image-upload-add-edit" className="cursor-pointer p-4 text-center">
                            <div className="mx-auto mb-1 text-zinc-500">
                              <svg className="mx-auto h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </div>
                            <span className="text-xs text-zinc-500">Add</span>
                          </label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </section>

              <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/15 text-emerald-400">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-zinc-100">Pricing & availability</h3>
                      <p className="text-xs text-zinc-500">Price (€) and stock per variant</p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-zinc-700 bg-zinc-950/60 px-3 py-1.5 text-xs text-zinc-400">
                    Total stock:{' '}
                    <span className="font-semibold tabular-nums text-zinc-100">{totalAvailableByVariant}</span>
                  </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-zinc-800">
                  <table className="w-full min-w-[520px] border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 bg-zinc-950/70">
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:px-4">Product</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:px-4">Variant</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:px-4">Price (€)</th>
                        <th className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-zinc-500 sm:px-4">Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(typePrices).flatMap(([typeKey, qPrices]) => {
                        const label = typeKey === '_single' ? 'All types' : (EDIT_PRODUCT_LABELS[typeKey] || typeKey);
                        const isTshirt = typeKey === 'tshirt';
                        const rowStock = typeStock[typeKey] || { standard: '0', premium: '0', deluxe: '0' };
                        const tiers = isTshirt
                          ? [
                              { key: 'standard', name: 'Standard', accent: 'emerald' },
                              { key: 'premium', name: 'Premium', accent: 'purple' },
                              { key: 'deluxe', name: 'Deluxe', accent: 'amber' }
                            ]
                          : [{ key: 'standard', name: 'Standard', accent: 'emerald' }];
                        const updateQPrice = (quality, value) => {
                          setTypePrices((prev) => ({ ...prev, [typeKey]: { ...prev[typeKey], [quality]: value } }));
                        };
                        const updateQty = (quality, raw) => {
                          const digits = raw.replace(/\D/g, '');
                          setTypeStock((prev) => ({
                            ...prev,
                            [typeKey]: {
                              standard: '0',
                              premium: '0',
                              deluxe: '0',
                              ...prev[typeKey],
                              [quality]: digits === '' ? '0' : digits
                            }
                          }));
                        };
                        return tiers.map((tier, idx) => {
                          const idBase = `${typeKey}-${tier.key}`;
                          const acc = tier.accent;
                          return (
                            <tr key={`${typeKey}-${tier.key}`} className="border-t border-zinc-800/80 bg-zinc-950/15">
                              {idx === 0 ? (
                                <td
                                  rowSpan={tiers.length}
                                  className="whitespace-nowrap px-3 py-3 align-middle text-sm font-medium text-zinc-200 sm:px-4"
                                >
                                  {label}
                                </td>
                              ) : null}
                              <td className="whitespace-nowrap px-3 py-3 align-middle text-zinc-400 sm:px-4">{tier.name}</td>
                              <td className="min-w-[9rem] px-3 py-2 align-middle sm:px-4">
                                <FloatingLabeledNumber
                                  id={`price-${idBase}`}
                                  label="Price"
                                  prefix="€"
                                  value={qPrices[tier.key]}
                                  min="0"
                                  step="0.01"
                                  accentRing={acc}
                                  onChange={(e) => updateQPrice(tier.key, e.target.value)}
                                />
                              </td>
                              <td className="min-w-[8rem] px-3 py-2 align-middle sm:px-4">
                                <FloatingLabeledNumber
                                  id={`qty-${idBase}`}
                                  label="Quantity"
                                  value={rowStock[tier.key]}
                                  min="0"
                                  step="1"
                                  accentRing={acc}
                                  onChange={(e) => updateQty(tier.key, e.target.value)}
                                />
                              </td>
                            </tr>
                          );
                        });
                      })}
                    </tbody>
                  </table>
                </div>

                <label className="mt-6 flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    id="chargeTax"
                    name="chargeTax"
                    checked={formData.chargeTax}
                    onChange={handleInputChange}
                    className="h-4 w-4 rounded border-zinc-600 bg-zinc-900 text-emerald-600 focus:ring-emerald-500/30"
                  />
                  <span className="text-sm text-zinc-400">Charge tax on this product</span>
                </label>
              </section>

              <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/15 text-sky-400">
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-zinc-100">Shipping & tracking</h3>
                    <p className="text-xs text-zinc-500">Quantities are set above with each variant</p>
                  </div>
                </div>

                <div className="mb-6 max-w-md">
                  <div className="flex items-end gap-2">
                    <div className="min-w-0 flex-1">
                      <FloatingLabeledNumber
                        id="edit-product-weight"
                        label="Weight"
                        value={formData.weight}
                        min="0"
                        step="0.1"
                        accentRing="emerald"
                        onChange={(e) => setFormData((prev) => ({ ...prev, weight: e.target.value }))}
                      />
                    </div>
                    <div className="flex w-28 shrink-0 flex-col pb-0.5">
                      <span className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-zinc-500">Unit</span>
                      <select
                        id="weightUnit"
                        name="weightUnit"
                        value={formData.weightUnit}
                        onChange={handleInputChange}
                        className="h-[46px] w-full rounded-lg border border-zinc-600 bg-zinc-950/90 px-2 text-sm text-zinc-200 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      >
                        <option value="kg">kg</option>
                        <option value="g">g</option>
                        <option value="lb">lb</option>
                        <option value="oz">oz</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {[
                    { id: 'trackQuantity', name: 'trackQuantity', label: 'Track quantity', checked: formData.trackQuantity },
                    { id: 'continueSellingOutOfStock', name: 'continueSellingOutOfStock', label: 'Continue selling when out of stock', checked: formData.continueSellingOutOfStock },
                    { id: 'isPhysicalProduct', name: 'isPhysicalProduct', label: 'Physical product (requires shipping)', checked: formData.isPhysicalProduct }
                  ].map((row) => (
                    <label key={row.id} htmlFor={row.id} className="flex cursor-pointer items-center gap-3 rounded-lg border border-transparent px-1 py-1 hover:border-zinc-800">
                      <input
                        type="checkbox"
                        id={row.id}
                        name={row.name}
                        checked={row.checked}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-zinc-600 bg-zinc-900 text-emerald-600 focus:ring-emerald-500/30"
                      />
                      <span className="text-sm text-zinc-400">{row.label}</span>
                    </label>
                  ))}
                </div>

                <div className="mt-6 border-t border-zinc-800 pt-5">
                  <label className="mb-3 flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      id="hasSKU"
                      name="hasSKU"
                      checked={formData.hasSKU}
                      onChange={handleInputChange}
                      className="h-4 w-4 rounded border-zinc-600 bg-zinc-900 text-emerald-600 focus:ring-emerald-500/30"
                    />
                    <span className="text-sm text-zinc-400">SKU or barcode</span>
                  </label>
                  {formData.hasSKU && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-zinc-500">SKU</label>
                        <input
                          type="text"
                          name="sku"
                          value={formData.sku}
                          onChange={handleInputChange}
                          className="w-full rounded-lg border border-zinc-700 bg-zinc-950/80 px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                          placeholder="SKU"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-zinc-500">Barcode</label>
                        <input
                          type="text"
                          name="barcode"
                          value={formData.barcode}
                          onChange={handleInputChange}
                          className="w-full rounded-lg border border-zinc-700 bg-zinc-950/80 px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                          placeholder="Barcode"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>

            <aside className="space-y-8 lg:col-span-1">
              <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
                <h3 className="mb-4 text-base font-semibold text-zinc-100">Status</h3>
                <select
                  name="status"
                  value={formData.status?.toLowerCase() || 'active'}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-950/80 px-4 py-3 text-sm font-medium text-zinc-100 focus:border-emerald-500/50 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </section>

              <section className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
                <div className="mb-4">
                  <h3 className="text-base font-semibold text-zinc-100">Organization</h3>
                  <p className="mt-0.5 text-xs text-zinc-500">Types, vendor, tags</p>
                </div>
                <div className="space-y-5">
                  <div>
                    <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-zinc-500">Product type</label>
                    <div className="flex flex-wrap gap-1.5 rounded-lg border border-zinc-800 bg-zinc-950/60 px-3 py-2.5">
                      {Object.keys(typePrices).length > 0 && Object.keys(typePrices)[0] !== '_single' ? (
                        Object.keys(typePrices).map((key) => (
                          <span
                            key={key}
                            className="rounded-md border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-200"
                          >
                            {EDIT_PRODUCT_LABELS[key] || key}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-zinc-500">{formData.category || 'Uncategorized'}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-zinc-500">
                      Vendor <span className="font-normal lowercase text-zinc-600">(streamer)</span>
                    </label>
                    {enableVendorEdit ? (
                      <select
                        name="vendor"
                        value={formData.vendor}
                        onChange={handleInputChange}
                        disabled={loadingVendors}
                        className="w-full rounded-lg border border-zinc-700 bg-zinc-950/80 px-3 py-2.5 text-sm text-zinc-100 focus:border-emerald-500/50 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
                      >
                        <option value="">{loadingVendors ? 'Loading…' : 'Select vendor'}</option>
                        {vendorOptions.map((vendor, index) => (
                          <option key={index} value={vendor} className="bg-zinc-900">
                            {vendor}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        name="vendor"
                        value={formData.vendor}
                        onChange={handleInputChange}
                        disabled
                        className="w-full cursor-not-allowed rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2.5 text-sm text-zinc-500 opacity-70"
                        placeholder="From submission"
                      />
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium uppercase tracking-wide text-zinc-500">Tags</label>
                    <input
                      type="text"
                      name="tags"
                      value={formData.tags}
                      onChange={handleInputChange}
                      disabled
                      className="w-full cursor-not-allowed rounded-lg border border-zinc-800 bg-zinc-950/50 px-3 py-2.5 text-sm text-zinc-500 opacity-70"
                      placeholder="From submission"
                    />
                  </div>
                </div>
              </section>
            </aside>
          </div>
          </form>
        </div>

        <footer className="shrink-0 border-t border-zinc-800 bg-zinc-900/95 px-6 py-4 sm:px-8">
          <div className="flex flex-col-reverse items-stretch justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-center text-xs text-zinc-500 sm:text-left">Review variant prices and stock before saving</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="rounded-lg border border-zinc-700 bg-zinc-900 px-5 py-2.5 text-sm font-medium text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.title.trim()}
                onClick={handleSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/20 transition-colors hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting && (
                  <svg className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {isSubmitting ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default EditProductModal; 